/**
 * A page's `where=` filter: Ask clauses in the address, parsed against live names, with a
 * setter that writes them back. Every page that lists things reads it the same way, so a
 * question answered in the palette opens as the same filter on the page.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import type { CostsState } from '@orgus/protocol';
import { useShallow } from 'zustand/react/shallow';
import { useOrgus } from '../../lib/store.ts';
import type { AskData } from '../ask/evaluate.ts';
import { parseAsk, whereText } from '../ask/parse.ts';
import type { AskEntity, Clause } from '../ask/parse.ts';

export interface Where {
  clauses: Clause[];
  /** The canonical text in the address. */
  text: string;
  setClauses: (c: Clause[]) => void;
  remove: (index: number) => void;
  clear: () => void;
}

export function useNames(): { machines: string[]; projects: string[] } {
  const machines = useOrgus(useShallow((s) => Object.values(s.machines).map((m) => m.name)));
  const projects = useOrgus(useShallow((s) => Object.values(s.projects).map((p) => p.name)));
  return useMemo(() => ({ machines, projects }), [machines, projects]);
}

export function useWhere(entity: AskEntity): Where {
  const [params, setParams] = useSearchParams();
  const raw = params.get('where') ?? '';
  const names = useNames();
  const clauses = useMemo(() => (raw ? parseAsk(raw, { ...names, defaultEntity: entity, fixedEntity: true }).clauses : []), [raw, names, entity]);
  const setClauses = useCallback(
    (c: Clause[]) =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          const t = whereText(c);
          if (t) p.set('where', t);
          else p.delete('where');
          p.delete('view');
          return p;
        },
        { replace: true, preventScrollReset: true },
      ),
    [setParams],
  );
  const remove = useCallback((i: number) => setClauses(clauses.filter((_, k) => k !== i)), [clauses, setClauses]);
  const clear = useCallback(() => setClauses([]), [setClauses]);
  return { clauses, text: whereText(clauses), setClauses, remove, clear };
}

const costInputs = new WeakMap<CostsState, Pick<AskData, 'sessionCost' | 'pricing'>>();

/**
 * What the costs module knows, for Ask: each session's priced total and the hub's price list.
 * Sessions it has no figure for are estimated from tokens at the same list prices.
 */
export function askCosts(c: CostsState | undefined): Pick<AskData, 'sessionCost' | 'pricing'> {
  if (!c) return {};
  let v = costInputs.get(c);
  if (!v) {
    const sessionCost: Record<string, number> = {};
    for (const s of c.sessions ?? []) sessionCost[s.sessionId] = s.total.cost;
    costInputs.set(c, (v = { sessionCost, pricing: c.pricing }));
  }
  return v;
}

/** The live data Ask predicates read. Re-renders when any of it changes. */
export function useAskData(now: number): AskData {
  const d = useOrgus(
    useShallow((s) => ({ sessions: s.sessions, machines: s.machines, projects: s.projects, checkouts: s.checkouts, pipelines: s.pipelines, audit: s.audit, costs: s.ext.costs as CostsState | undefined })),
  );
  return useMemo(() => {
    const { costs, ...rest } = d;
    return { ...rest, ...askCosts(costs), now };
  }, [d, now]);
}

/** A snapshot for one-off answers (the palette). */
export function askSnapshot(now = Date.now()): AskData {
  const s = useOrgus.getState();
  return { sessions: s.sessions, machines: s.machines, projects: s.projects, checkouts: s.checkouts, pipelines: s.pipelines, audit: s.audit, ...askCosts(s.ext.costs as CostsState | undefined), now };
}
